import { useContext, useEffect, useState } from "react";
import { Formik, Form, FormikProps, getIn } from "formik";
import { Button, Spinner } from "reactstrap";
import "./Login.scss";
import FormInput from "../../../components/FormInput";
import { validationSchema } from "../../../validations/login.validation";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import { loginWithUsernameAndPassword } from "../../../services/auth.service";
import { toast } from "react-hot-toast";
import useLocalStorage from "../../../hooks/useLocalStorage";
import Logo from "../../../assets/images/logo.jpeg";
import FormSelect from "../../../components/FormSelect";
import { getCompanies } from "../../../services/company.service";
import { CompanyContext } from "../../../contexts/CompanyContext";

interface LoginFormValues {
  username: string;
  password: string;
  company: any;
  remember: boolean;
}

function Login() {
  const [rememberedData, setRememberedData] = useLocalStorage<{ username: string; company: any }>(
    "remembered-login",
  );
  const [, setRefreshToken] = useLocalStorage("refresh-token");
  const [, setAccessToken] = useLocalStorage("access-token");
  const [, setStoredUser] = useLocalStorage<any>("user");

  const [companies, setCompanies] = useState([]);
  const { handleChangeCompany }: any = useContext(CompanyContext);
  const { updateAuthentication } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    getCompanies({})
      .then((response) => {
        setCompanies(response.data.results);
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Network error while loading companies.";
        toast.error(message);
      });
  }, []);

  // Define the initial form values
  const initialValues: LoginFormValues = {
    username: rememberedData?.username || "",
    password: "",
    company: rememberedData?.company || "",
    remember: Boolean(rememberedData),
  };

  // Handle form submission
  const handleSubmit = (
    values: LoginFormValues,
    { setSubmitting }: FormikProps<LoginFormValues>,
  ) => {
    handleChangeCompany(values.company);
    if (values.remember) {
      setRememberedData({ username: values.username, company: values.company });
    } else {
      setRememberedData(null);
    }

    loginWithUsernameAndPassword({
      username: values.username,
      password: values.password,
      company: values.company,
    })
      .then((response) => {
        setRefreshToken(response.data.tokens.refresh.token);
        if (response.data.tokens?.access?.token) {
          setAccessToken(response.data.tokens.access.token);
        }
        const loggedInUser = response.data.user;
        setStoredUser(loggedInUser);
        updateAuthentication(true);
        const role = loggedInUser?.role;
        navigate(role === "warehouse" ? "/checker-panel" : "/");
      })
      .catch((error: any) => {
        setSubmitting(false);
        const message =
          error?.response?.data?.message || error?.message || "Login failed. Please try again.";
        toast.error(message);
      });
  };

  return (
    <div className='login-form-container'>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values: any, actions: any) => handleSubmit(values, actions)}
      >
        {(props: FormikProps<LoginFormValues>) => {
          const { values, touched, errors, isSubmitting, handleChange, setFieldValue } = props;
          return (
            <Form className='login-form pt-0' autoComplete='on'>
              <div className='login-form__welcome-text'>
                <img src={Logo} alt='Logo' className='img-fluid' width={130} />
                <h2>Welcome Back</h2>
                <p>Sign in to your account to continue</p>
              </div>

              <FormInput
                name='username'
                type='text'
                label='Username'
                placeholder='Enter username'
                autoComplete='username'
                value={values.username}
                onChange={handleChange}
                error={errors.username}
                touched={touched.username}
              />
              <FormInput
                name='password'
                type='password'
                label='Password'
                placeholder='Enter password'
                autoComplete='current-password'
                value={values.password}
                onChange={handleChange}
                error={errors.password}
                touched={touched.password}
              />

              <FormSelect
                id='company'
                name='company'
                label='Company'
                placeholder='Select company'
                formGroupClasses='mb-2'
                options={companies}
                onChange={(name: string, value: object) => {
                  setFieldValue(name, value);
                }}
                value={getIn(values, "company")}
                error={getIn(errors, "company")}
                touched={getIn(touched, "company")}
              />

              <div className='forgot-password-remember-me-container'>
                <div className='form-check'>
                  <input
                    id='remember'
                    name='remember'
                    type='checkbox'
                    className='form-check-input'
                    checked={values.remember}
                    onChange={handleChange}
                  />
                  <label className='form-check-label' htmlFor='remember'>
                    Remember me
                  </label>
                </div>
              </div>

              <div className='login-form__button-container'>
                <Button
                  className={isSubmitting ? "login-form__button-container--submitting" : ""}
                  type='submit'
                  color='primary'
                  disabled={isSubmitting}
                  block
                >
                  {isSubmitting ? <Spinner size='sm' /> : "Sign In"}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

export default Login;
